<template>
    <div
        class="card notranslate"
        v-bind:class="viewGa"
    >
        <div class="card_content" v-on:click="handleCombinationClick">
            <div class="card_content_header">
                <div class="subTitle">
                    {{ leagueName }}
                </div>
                <span class="title">
                    <SboIcon
                        name="sports:soccerColor"
                        size="16"
                    />
                    <template v-if="combinationWithEvent.event && combinationWithEvent.event.isLive">
                        <span class="liveBadge">{{ liveBadgeText }}</span>
                        <span class="score">[{{ combinationWithEvent.event.liveScore.home }}:{{ combinationWithEvent.event.liveScore.away }}]</span>
                    </template>
                    <span class="teamVsName">{{ homeTeamName }} - {{ awayTeamName }}</span>
                </span>
            </div>
            <ul>
                <BetBuilderCombinationTicket
                    v-for="item in visibleItems"
                    v-bind:key="item.selection.id"
                    v-bind:selection="item.selection"
                    v-bind:market="item.market"
                    v-bind:event="event"
                />
            </ul>
            <div
                v-if="(combination.selectionIds?.length ?? 0) > 2"
                v-bind:class="expandBtnClass"
                v-on:click.stop="toggleExpand"
            >
                {{ toggleDetail }}
                <SboIcon
                    v-bind:name="isExpanded ? 'system:arrowUp' : 'system:arrowDown'"
                    size="14"
                />
            </div>
        </div>
        <BetBuilderCombinationFooter
            v-bind:maxOdds="maxOdds"
            v-bind:oddsChanging="oddsChanging"
            v-bind:isSuspended="isSuspended"
            v-bind:addToSlipGa="addToSlipGa"
            v-on:addToSlip="addToBetSlip"
        />
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, onUnmounted, ref, watch } from 'vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import BetBuilderCombinationFooter from '@/components/recommendations/BetBuilderCombinationFooter.vue';
    import BetBuilderCombinationTicket from '@/components/recommendations/BetBuilderCombinationTicket.vue';
    import { BetBuilderCombinationsContext } from '@/components/recommendations/BetBuilderCombinationsContext';
    import type { IBetBuilderCombination, IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
    import { BetBuilderSelectionStatus, BetPageType, MarketTab } from '@/interface/enum';
    import { platform } from '@/libraries/helpers/platformHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import { useRootStore } from '@/stores/rootStore';

    interface Props {
        combination: IBetBuilderRecommendation;
        order: number;
    }

    const props = defineProps<Props>();

    const { combinationWithEventByKey, isExpanded, toggleExpand } = BetBuilderCombinationsContext.inject();

    const combinationWithEvent = computed<IBetBuilderCombination>(() => (
        combinationWithEventByKey.value[props.combination.combinationKey] ?? {
            event: null,
            selections: [],
            maxOdds: props.combination.maxOdds,
            isValid: false,
        }
    ));

    const MAX_VISIBLE_ITEMS = 2;

    const visibleItems = computed(() => {
        if (isExpanded.value) {
            return combinationWithEvent.value.selections;
        }

        return combinationWithEvent.value.selections.slice(0, MAX_VISIBLE_ITEMS);
    });

    const homeTeamName = computed(() => getTranslated(combinationWithEvent.value.event?.homeTeam.teamName));
    const awayTeamName = computed(() => getTranslated(combinationWithEvent.value.event?.awayTeam.teamName));
    const leagueName = computed(() => getTranslated(combinationWithEvent.value.event?.leagueName));
    const liveBadgeText = computed(() => i18n.t('live'));
    const maxOdds = computed(() => formatMinDecimalPlace(combinationWithEvent.value.maxOdds, 2));
    const toggleDetail = computed(() => (isExpanded.value ? i18n.t('cta_show_less') : i18n.t('cta_show_more')));
    const expandBtnClass = computed(() => ['card_content_more', isExpanded.value ? 'PreBuiltComb_Btn_More_Collapse' : 'PreBuiltComb_Btn_More_Expand']);

    const event = computed(() => combinationWithEvent.value.event);
    const { goToMatchDetail } = useMatchFunction(event);

    function handleCombinationClick() {
        goToMatchDetail({ marketTab: MarketTab.BetBuilder });
    }

    const isSuspended = computed(() => combinationWithEvent.value.selections.some(item => item.selection.status === BetBuilderSelectionStatus.Suspended));
    const viewGa = computed(() => `PreBuiltComb_${props.order}_View`);
    const addToSlipGa = computed(() => `PreBuiltComb_${props.order}_Btn_AddToSlip`);

    const { addTicket, removeBetSlip, setOpenFrom } = useBetBuilderBetSlipStore();
    const { showMediumBetSlip } = useBetSlipStore();
    const { resetSingleModeForBetBuilder } = useSingleModeBetSlipStore();

    const oddsChanging = ref(false);
    let timeoutId: number;

    watch(maxOdds, (newVal, oldVal) => {
        if (newVal !== oldVal) {
            oddsChanging.value = true;

            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
                oddsChanging.value = false;
            }, 3000);
        }
    });

    onUnmounted(() => {
        clearTimeout(timeoutId);
    });

    const { isAsi } = storeToRefs(useRootStore());

    async function addToBetSlip() {
        if (!isAsi.value) {
            platform.showLogin('Sports');
            return;
        }

        const currentEvent = event.value;
        if (isSuspended.value) return;
        if (!currentEvent || combinationWithEvent.value.selections.length !== (props.combination.selectionIds?.length ?? 0)) return;

        await removeBetSlip();
        resetSingleModeForBetBuilder();

        combinationWithEvent.value.selections.forEach(({ selection, market }) => {
            addTicket(selection, currentEvent, market);
        });

        setOpenFrom(BetPageType.BetBuilderRecommendation);
        showMediumBetSlip();
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/quickCombination";
@import "@/scss/components/quickCombinationTicket";
</style>
